import { ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import useAuth, { AuthContextType } from '@contexts/useAuth/useAuth';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

/**
 * Shape of the AuthUserSecurityMe readmodel push.
 * Mirrors `IdentityAuthUserSecurityMeQuery` from teselly-client-library.
 */
export type AuthUserSecurityMe = {
  id: string;
  userId: string;
  accountId: string;
  ownerId?: string;
  email: string;
  passkeys: ({
    credentialId: string;
    deviceName?: string;
    registeredAt: string;
  })[];
  hasPassword: boolean;
  googleMfaVerified: boolean;
  memberships?: {
    [key: string]: ( string )[] | undefined;
  };
  updatedAt: string;
};

export type AuthMembershipsContextType = {
  security: AuthUserSecurityMe | null;
  isLoading: boolean;
  error: Error | null;
};

const authMembershipsContext = createContext<AuthMembershipsContextType | undefined>( undefined );

type ProvideAuthMembershipsProps = {
  children: ReactNode;
  onMembershipChange?: ( security: AuthUserSecurityMe ) => void;
};

// Bounded retry budget for the initial query below — mirrors
// `TessellyFeatureFlagProvider`'s `REFETCH_MAX_ATTEMPTS` / `REFETCH_RETRY_MS`
// (20 attempts @ 750ms, ~15s) so both post-login recoveries degrade on the
// same timeline.
const SECURITY_QUERY_MAX_ATTEMPTS = 20;
const SECURITY_QUERY_RETRY_MS = 750;

export const ProvideAuthMemberships = ({ children, onMembershipChange }: ProvideAuthMembershipsProps ) => {
  const { backend } = useBackend() as BackendContextType;
  const { user } = useAuth() as AuthContextType;
  const [ security, setSecurity ] = useState<AuthUserSecurityMe | null>( null );
  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ error, setError ] = useState<Error | null>( null );
  const onMembershipChangeRef = useRef( onMembershipChange );

  useEffect(() => {
    onMembershipChangeRef.current = onMembershipChange;
  }, [onMembershipChange]);

  useEffect(() => {
    if ( !backend || !user?.id ) {
      return;
    }
    setIsLoading( true );
    setError( null );

    let closed = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    // QUERY-FIRST, then subscribe — mirrors the feature-flag provider's pattern.
    // PV3 realtime subscriptions deliver only *subsequent* updates; for a
    // pre-provisioned account (and in E2E generally) the initial WS snapshot never
    // arrives, which left `isLoading` stuck `true` and `onMembershipChange` — the
    // trigger for the post-login feature-flag refetch (`provider.refetchFlags()`) —
    // never firing, so the account-scoped flags never loaded and
    // `ProtectedHomeRedirect` hung on its loader at `/`. An HTTP query reliably
    // carries the session cookie and returns the current row, so seed from it
    // first, then subscribe for live updates.
    //
    // The query itself lands right at the post-login provisioning window, where
    // this route deliberately 503s while `accountId` isn't bound yet (see
    // featureFlags.readmodel.ts's `ServiceUnavailableError` for the same
    // window). A one-shot attempt left `security` permanently null and
    // `onMembershipChange` never fired — which meant `refetchFlags()` never ran
    // and `ProtectedHomeRedirect` hung on `/` forever, even though the session
    // was fully valid a moment later. Retry with the same bounded backoff the
    // feature-flag provider uses, instead of giving up after one attempt.
    const fetchSecurity = ( attempt: number ): void => {
      backend.queries.IdentityAuthUserSecurityMe({})
        .then(( next ) => {
          if ( closed ) return;
          if ( next?.accountId ) {
            setSecurity( next as AuthUserSecurityMe );
            onMembershipChangeRef.current?.( next as AuthUserSecurityMe );
            setIsLoading( false );
            return;
          }
          // Truthy-but-empty payload is projection lag right after login —
          // keep retrying rather than settling `isLoading` on a null security.
          throw new Error( 'AuthUserSecurityMe not yet provisioned' );
        })
        .catch(( err ) => {
          if ( closed ) return;
          if ( attempt < SECURITY_QUERY_MAX_ATTEMPTS ) {
            retryTimer = setTimeout(() => {
              retryTimer = null;
              fetchSecurity( attempt + 1 );
            }, SECURITY_QUERY_RETRY_MS );
            return;
          }
          setError( err as Error );
          setIsLoading( false );
        });
    };
    fetchSecurity( 0 );

    let subscription: { close: () => void } | undefined;
    try {
      subscription = backend.subscriptions.IdentityAuthUserSecurityMe(
        { _snapshot: true },
        ( change: ReadmodelChange<AuthUserSecurityMe> ) => {
          if ( change.type === 'delete' ) {
            setSecurity( null );
          } else if ( change.type === 'snapshot' ) {
            const next = change.data.items[0];
            if ( next ) {
              setSecurity( next );
              onMembershipChangeRef.current?.( next );
            }
          } else if ( change.type === 'insert' || change.type === 'update' ) {
            setSecurity( change.data );
            onMembershipChangeRef.current?.( change.data );
          }
          setIsLoading( false );
        }
      );
    } catch ( err ) {
      if ( !closed ) {
        setError( err as Error );
        setIsLoading( false );
      }
    }

    return () => {
      closed = true;
      if ( retryTimer ) clearTimeout( retryTimer );
      subscription?.close();
    };
  }, [ backend, user?.id ]);

  return (
    <authMembershipsContext.Provider value={{
      security,
      isLoading,
      error
    }}>
      {children}
    </authMembershipsContext.Provider>
  );
};

export const useAuthMemberships = () => useContext( authMembershipsContext );

export default useAuthMemberships;
